import React, { useEffect, useState } from 'react';
import { IPivotStyles, Pivot, PivotItem } from '@fluentui/react';
import WorkOrderGeneralInformationComponent from './work-order-general-information.component';
import WorkOrderWorksComponent from './work-order-works.component';
import WorkOrderWorkPartsComponent from './work-order-work-parts.component';
import WorkOrderDocumentsComponent from './work-order-documents.component';
import WorkPartCreateComponent from '../work-part/work-part-create.component';
import ErrorMessageComponent from '../error-message/error-message.component';

import ApiService from '../../services/api/api.service';
import useAuthStatus from '../../hooks/auth/AuthHook';

import ItemOrdenDeTrabajo from '../../models/ItemOrdenDeTrabajo';
import ItemTarea from '../../models/ItemTarea';
import ItemPermiso from '../../models/ItemPermiso';
import ItemRegistroTiempo from '../../models/ItemRegistroTiempo';
import ElementEnum from '../../enums/ElementEnum';

const pivotStyles: IPivotStyles = {
    root: {},
    link: {},
    linkContent: {},
    text: {},
    count: {},
    icon: {},
    linkInMenu: {},
    overflowMenuButton: {},
    linkIsSelected: {
        selectors: {
            '::before': {
                backgroundColor: "var(--color-primario)",
            },
        },
    },
};

interface IWorkOrderComponentProps {
    workOrder?: ItemOrdenDeTrabajo;
    setLoading: (value: boolean) => void;
};

const WorkOrderComponent = (props: IWorkOrderComponentProps) => {
    const auth = useAuthStatus();
    const workPermission : ItemPermiso = auth.getPermission(ElementEnum.WORKS);

    const [selectedKey, setSelectedKey] = useState<string>("1");
    const [workOrder, setWorkOrder] = useState<ItemOrdenDeTrabajo | undefined>(props.workOrder);
    const [error, setError] = useState<string>('');
    const [editingWorkPart, setEditingWorkPart] = useState<ItemRegistroTiempo | undefined>(undefined);
    const [editingWorkForPart, setEditingWorkForPart] = useState<ItemTarea | undefined>(undefined);

    const onLinkClick = (item?: PivotItem) : void => {
        if (item && item.props.itemKey) {
            setSelectedKey(item.props.itemKey);
        }
    };

    const updateWorkOrder = () : void => {
        if (auth.user && workOrder) {
            props.setLoading(true);
            ApiService.Post<ItemOrdenDeTrabajo>("OrdenesTrabajo", "Get", auth.user, {
                "IdOrden": workOrder.Id,
            }).then((response: ItemOrdenDeTrabajo) => {
                setWorkOrder(response);
                props.setLoading(false);
            }, (error: string) => {
                setError(error);
                props.setLoading(false);
            });
        }
    };

    const onWorkPartClick = (item: ItemRegistroTiempo) : void => {
        if (!workOrder) return;
        const tarea = workOrder.ListaTareas.find((t: ItemTarea) => t.Id === item.IdTarea);
        if (tarea) {
            setEditingWorkForPart(tarea);
            setEditingWorkPart(item);
        }
    };

    useEffect(() => {
        setWorkOrder(props.workOrder);
    }, [props.workOrder]);

    return (
        <>
            {
                workOrder ? (
                    <>
                        <Pivot
                            overflowBehavior="menu"
                            styles={pivotStyles}
                            aria-label="Pivot acciones sobre una orden de trabajo en concreto"
                            headersOnly={true}
                            selectedKey={selectedKey}
                            onLinkClick={onLinkClick}>
                            <PivotItem 
                                itemKey='1' 
                                headerText="Información general" />
                            {
                                workPermission.Ver ? (
                                    <PivotItem 
                                        itemKey='2' 
                                        headerText="Actuaciones" />
                                ) : (
                                    null
                                )
                            }
                            {
                                workPermission.Ver ? (
                                    <PivotItem 
                                        itemKey='3' 
                                        headerText="Registro de tiempos" />
                                ) : (
                                    null
                                )
                            }
                            {
                                workPermission.Ver ? (
                                    <PivotItem 
                                        itemKey='4' 
                                        headerText="Firmas y documentos" />
                                ) : (
                                    null
                                )
                            }
                        </Pivot>
                        {
                            (selectedKey === "1") ? (
                                <PivotItem>
                                    <WorkOrderGeneralInformationComponent 
                                        workOrder={workOrder} />
                                </PivotItem>
                            ) : (selectedKey === "2") ? (
                                workPermission.Ver ? (
                                    <PivotItem>
                                        <WorkOrderWorksComponent 
                                            workOrder={workOrder}
                                            onWorkCreated={() => updateWorkOrder()}
                                            onWorkEdited={() => updateWorkOrder()}
                                            onWorkDeleted={() => updateWorkOrder()}
                                            onWorkPartCreated={() => updateWorkOrder()}
                                            onWorkDocumentCreated={() => updateWorkOrder()} />
                                    </PivotItem>
                                ) : (
                                    null
                                )
                            ) : (selectedKey === "3") ? (
                                workPermission.Ver ? (
                                    <PivotItem>
                                        <WorkOrderWorkPartsComponent
                            workParts={workOrder.ListaPartesTrabajo}
                            onWorkPartClick={onWorkPartClick} />
                                    </PivotItem>
                                ) : (
                                    null
                                )
                            ) : (selectedKey === "4") ? (
                                workPermission.Ver ? (
                                    <PivotItem>
                                        <WorkOrderDocumentsComponent documents={workOrder.ListaDocumentos || []} />
                                    </PivotItem>
                                ) : (
                                    null
                                )
                            ) : (
                                null
                            )
                        }
                    </>
                ) : (error) ? (
                    <ErrorMessageComponent message={error} />
                ) : (
                    null
                )
            }
            {editingWorkPart && editingWorkForPart && (
                <WorkPartCreateComponent
                    work={editingWorkForPart}
                    workPart={editingWorkPart}
                    onClose={() => { setEditingWorkPart(undefined); setEditingWorkForPart(undefined); }}
                    onWorkPartCreatedOrEdited={() => updateWorkOrder()} />
            )}
        </>
    );
};

export default WorkOrderComponent;
